import { UseMutateFunction, useMutation, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  AiDebugProcess,
  apiClient,
  AuthorRole,
  CreateFixedPromptTextGenerationOut,
  DialogueSnapshot,
  Message,
} from 'lib/apiClient'
import { QueryKey } from 'lib/queryClient'
import { convertDialogueSnapshotsToMessageList } from 'lib/utils/messages'

interface SelectedProcessOption {
  label: string
  value: string
  key: string
}

export interface ProcessDebugDetailsHook {
  chatMessages: Message[]

  selectedMessageId: string | null
  setSelectedMessageId: (correlationId: string) => void

  dialogueSnapshots: DialogueSnapshot[]
  isLoadingDialogueSnapshots: boolean
  isDialogueSnapshotsError: boolean

  debugProcesses: AiDebugProcess[]
  isLoadingDebugProcesses: boolean
  isDebugProcessesError: boolean

  selectedProcess: string | null
  selectedProcessLabel: string | null
  setSelectedProcess: Dispatch<SetStateAction<string | null>>
  selectedProcessData: AiDebugProcess | null
  debugProcessOptions: SelectedProcessOption[]

  onGenerate: UseMutateFunction<
    CreateFixedPromptTextGenerationOut,
    Error,
    string,
    unknown
  >
  isGeneratePending: boolean
  onPrevClick: () => void
  onNextClick: () => void
  currentFixedPromptTextGeneration: FixedPromptTextGeneration | undefined
  currentFixedPromptTextGenerationIndex: number
  fixedPromptTextGenerationsCount: number
}

interface FixedPromptTextGeneration {
  id: string
  content: string
  current: boolean
}

export function useProcessDebugDetails(): ProcessDebugDetailsHook {
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null)
  const [selectedProcess, setSelectedProcess] = useState<string | null>(null)
  const [selectedProcessLabel, setSelectedProcessLabel] = useState<string | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()

  const correlationIds = useMemo(
    () => searchParams.getAll('correlationId'),
    [searchParams],
  )

  const [fixedPromptTextGenerations, setFixedPromptTextGenerations] = useState<
    FixedPromptTextGeneration[]
  >([])

  const {
    data: dialogSnapshotData,
    error: dialogueSnapshotsError,
    isLoading: isLoadingDialogueSnapshots,
  } = useQuery({
    // only fetch on mount, not when search params refresh
    queryKey: [QueryKey.getDialogueSnapshot],
    queryFn: () => apiClient.getSnapshot(correlationIds, null, true),
    enabled: Boolean(correlationIds.length),
  })

  const chatMessages = useMemo(
    () => convertDialogueSnapshotsToMessageList(dialogSnapshotData?.dialogueSnapshots),
    [dialogSnapshotData?.dialogueSnapshots],
  )

  useEffect(() => {
    const selectedMessage = chatMessages.find(
      message =>
        message.authorRole === AuthorRole.Customer &&
        correlationIds.includes(message.messageId),
    )
    if (selectedMessage) {
      setSelectedMessageId(selectedMessage.messageId)
    }
  }, [chatMessages, correlationIds])

  const {
    data: debugProcessData,
    error: debugProcessError,
    isLoading: isLoadingDebugProcesses,
  } = useQuery({
    queryKey: [QueryKey.getAiDebug, selectedMessageId],
    queryFn: () =>
      apiClient.getAiDebug(selectedMessageId ? [selectedMessageId] : [], null),
    enabled: Boolean(selectedMessageId),
    // forcing fetch everytime
    gcTime: 0,
  })

  useEffect(() => {
    setSelectedProcess(debugProcessData?.debug?.[0]?.id ?? null)
    setSelectedProcessLabel(debugProcessData?.debug?.[0]?.label ?? null)
  }, [debugProcessData?.debug])

  const selectedProcessData = useMemo(
    () => debugProcessData?.debug.find(({ id }) => id === selectedProcess) ?? null,
    [debugProcessData?.debug, selectedProcess],
  )

  const { mutate: onGenerate, isPending: isGeneratePending } = useMutation({
    mutationFn: (promptContent: string) =>
      apiClient.createFixedPromptTextGeneration({
        support_text_generation_id: selectedProcessData?.id!,
        correlation_id: uuidv4(),
        prompt_content: promptContent,
      }),
    onSuccess(data, content) {
      setFixedPromptTextGenerations(ids =>
        ids
          .map(item => ({ ...item, current: false }))
          .concat({
            id: data.fixedPromptTextGenerationId,
            content,
            current: true,
          }),
      )
    },
  })

  const onPrevClick = useCallback(() => {
    setFixedPromptTextGenerations(ids => {
      const currentIndex = ids.findIndex(item => item.current)

      if (currentIndex <= 0) {
        return ids
      }

      return ids.map((item, index) => ({ ...item, current: index === currentIndex - 1 }))
    })
  }, [])

  const onNextClick = useCallback(() => {
    setFixedPromptTextGenerations(ids => {
      const currentIndex = ids.findIndex(item => item.current)

      if (currentIndex === -1 || currentIndex === ids.length - 1) {
        return ids
      }

      return ids.map((item, index) => ({ ...item, current: index === currentIndex + 1 }))
    })
  }, [])

  const debugProcessOptions = useMemo(
    () =>
      debugProcessData?.debug.map(({ id, label }) => ({
        label,
        value: id,
        key: id,
      })) ?? [],
    [debugProcessData?.debug],
  )

  const handleSelectMessageId = (correlationId: string) => {
    setSelectedMessageId(correlationId)
    setSearchParams({ correlationId }, { replace: true })
  }

  return {
    chatMessages,

    selectedMessageId,
    setSelectedMessageId: handleSelectMessageId,

    dialogueSnapshots: dialogSnapshotData?.dialogueSnapshots ?? [],
    isLoadingDialogueSnapshots,
    isDialogueSnapshotsError: Boolean(dialogueSnapshotsError),

    debugProcesses: debugProcessData?.debug ?? [],
    isLoadingDebugProcesses,
    isDebugProcessesError: Boolean(debugProcessError),

    selectedProcess,
    selectedProcessLabel,
    setSelectedProcess,
    selectedProcessData,
    debugProcessOptions,

    onGenerate,
    isGeneratePending,
    onPrevClick,
    onNextClick,
    currentFixedPromptTextGeneration: fixedPromptTextGenerations.find(
      item => item.current,
    ),
    currentFixedPromptTextGenerationIndex: fixedPromptTextGenerations.findIndex(
      item => item.current,
    ),
    fixedPromptTextGenerationsCount: fixedPromptTextGenerations.length,
  }
}
